import { useState, useEffect, useContext } from "react";
import { OAuth2 } from "components";
import ReCAPTCHA from "react-google-recaptcha";
import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import jwtDecode from "jwt-decode";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  SnackbarContent,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslations, useUser, useForm } from "hooks";
import { getTranslation } from "common";
import { noTokenApi, usersApi } from "api";
import { isEmail, isRequired, Validation } from "hooks/useForm";
import FlowentiLogo from "../image/Logo.svg"; //# use this cause is without background
import { JWTData } from "common/utilities";
import { removeSelectedOrganization } from "Redux/organizationReducer";
import { AuthContext } from "contexts";

const Login = () => {
  const [searchParams] = useSearchParams();
  const emailParam = searchParams.get("email");
  const translations = useTranslations();
  const user = useUser();
  const {
    actions: { setJwt, setUser },
  } = useContext(AuthContext);
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [exist, setExist] = useState<boolean>(false);

  useEffect(() => {
    if (!user) {
      dispatch(removeSelectedOrganization(""));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    (async () => {
      if (emailParam) {
        const response = await usersApi.get(`emailinplatform/${emailParam}`);
        setExist(response.data.mailexist);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validations: Validation[] = [
    ({ email }) =>
      isRequired(email) || {
        email: getTranslation(translations, "register.valid.Error_after_at"),
      },
    ({ captcha }) =>
      isRequired(captcha) ||
      process.env.NODE_ENV === "development" ||
      Boolean(window.Cypress) || {
        captcha: getTranslation(translations, "login.valid.captchaError"),
      },
    ({ email }) =>
      isEmail(email) || {
        email: getTranslation(translations, "register.valid.Error_after_at"),
      },
  ];

  const { values, changeHandler, errors, touched, isValid } = useForm(
    {
      email: emailParam ?? "",
      password: "",
      captcha: null,
    },
    validations
  );

  const submitData = async (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    event.preventDefault();
    const { email, password, captcha } = values;
    if (!captcha && process.env.NODE_ENV === "production" && !window.Cypress)
      return;
    try {
      const response = await noTokenApi.post("login", {
        jwtusername: email,
        jwtpassword: password,
      });
      setJwt(response.data.token);
      const decoded: JWTData = jwtDecode(response.data.token);
      const id = decoded.userid;
      let result = await usersApi.get(`${id}`);
      setUser({
        lastEventID: Number(decoded["last-event-id"]),
        jwt: response.data.token,
        userId: result.data.user.id,
        email: result.data.user.email,
        username: result.data.user.username,
        firstname: result.data.user.firstname,
        lastname: result.data.user.lastname,
        picture: result.data.user.picture,
        linkprofile: result.data.user.linkprofile,
        subscriptiontype: result.data.user.subscriptiontype,
        i18n: result.data.user.i18n,
        dob: result.data.user.dob,
      });
      navigate("/dashboard/organizations");
    } catch {
    } finally {
    }
  };

  return (
    <Container
      maxWidth="xs"
      sx={{
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Card elevation={10}>
        <Stack
          direction="row"
          px={2}
          alignItems="center"
          sx={{ backgroundColor: (_) => _.palette.primary.main }}
        >
          <Box
            component="img"
            alt="..."
            mr={2}
            style={{
              width: "48px",
              height: "48px",
            }}
            src={FlowentiLogo}
          />
          <Typography sx={{ flex: 1 }} variant="h4" color="white">
            Flowenti
          </Typography>
        </Stack>
        <CardContent>
          <Stack spacing={2}>
            {exist && (
              <SnackbarContent
                message={getTranslation(
                  translations,
                  "register.label.email_is_present_on_platform"
                )}
              />
            )}
            <Stack component="form" spacing={2}>
              <TextField
                id="email-login"
                size="small"
                label={getTranslation(translations, "user.email.label")}
                type="email"
                autoComplete="off"
                fullWidth
                value={values.email}
                helperText={touched.email && errors.email}
                error={Boolean(touched.email && errors.email)}
                onChange={({ target }) => changeHandler("email", target.value)}
              />
              <TextField
                id="password-login"
                size="small"
                label="Password"
                type="password"
                autoComplete="off"
                fullWidth
                value={values.password}
                helperText={touched.password && errors.password}
                error={Boolean(touched.password && errors.password)}
                onChange={({ target }) =>
                  changeHandler("password", target.value)
                }
              />
              <Stack mt={6} direction="row" justifyContent="center">
                {process.env.NODE_ENV === "production" && !window.Cypress && (
                  <ReCAPTCHA
                    id="recaptcha"
                    theme={theme.palette.mode}
                    onChange={(_: any) => changeHandler("captcha", _)}
                    sitekey={"6LeTBVspAAAAAPds-GYTsoJaZJ3ean2pXKNNX2A6"} //TO-DO change secret key and encrypt it
                    // sitekey={"6LdHCIcgAAAAACBCES02Ox2r8sDeYmUn60RPeJIX"} //TO-DO change secret key and encrypt it
                  />
                )}
              </Stack>
              <Stack mt={6} direction="row">
                <Button
                  id="button-login"
                  color="primary"
                  fullWidth
                  size="small"
                  disabled={!isValid}
                  variant="contained"
                  type="submit"
                  onClick={submitData}
                >
                  {getTranslation(translations, "register.signin.label")}
                </Button>
              </Stack>
            </Stack>
            <OAuth2 login />
          </Stack>
        </CardContent>
      </Card>
    </Container>
  );
};

export default Login;
